import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { RepairRequest, UserRole } from '../types/firestore';
import { repairRequestService } from '../services/repairRequestService';
import { getUserRole } from '../utils/accessControl';
import RepairRequestCard from '../components/Request/RepairRequestCard';
import {
  PageEmptyState,
  PageErrorState,
  PageLoadingState,
  PageNoResultsState,
} from '../components/common/ViewStates';
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import {
  getClientRequestBuckets,
  getClientRequestListBucket,
} from '../utils/repairRequestWorkflow';
import { getRepairRequestId } from '../utils/repairRequestReadModel';
import {
  getRequestQueueFilterActiveRailClasses,
  getRequestQueueFilterCountClasses,
  getRequestQueueFilterTabClasses,
  type RequestQueueTone,
} from '../utils/requestQueueVisuals';

type ClientListFilter = 'needsAction' | 'active' | 'completed';
type ClientFilterTone = Exclude<RequestQueueTone, 'neutral'>;
type ClientQueueBucket = 'attention' | 'in_progress' | 'history';

const CLIENT_FILTER_QUEUE_BUCKET: Record<ClientListFilter, ClientQueueBucket> = {
  needsAction: 'attention',
  active: 'in_progress',
  completed: 'history',
};

const RequestListSkeleton: React.FC = () => (
  <div className="space-y-2.5 animate-pulse sm:space-y-3">
    {[1, 2, 3].map((item) => (
      <div
        key={item}
        className="flex min-h-[132px] flex-col rounded-lg border border-primary-dark-300/16 bg-primary-dark-100/10 p-2.5 sm:min-h-[136px] sm:p-3"
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="h-4 w-36 rounded bg-primary-dark-300/85" />
          <div className="h-5 w-20 rounded-full bg-primary-dark-300/85" />
        </div>
        <div className="mb-2 h-3 w-60 rounded bg-primary-dark-300/85" />
        <div className="mb-2.5 h-12 w-full rounded-md bg-primary-dark-300/70" />
        <div className="mb-3 h-8 w-4/5 rounded bg-primary-dark-300/70" />
        <div className="mt-auto h-8 w-full rounded-md bg-primary-dark-300/65" />
      </div>
    ))}
  </div>
);

const ClientRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userProfile, claims } = useAuth();
  const [requests, setRequests] = useState<RepairRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<ClientListFilter | null>(() =>
    getClientFilterFromSearch(location.search)
  );
  const [activeRequestId, setActiveRequestId] = useState<string | null>(() =>
    getClientRequestFromSearch(location.search)
  );
  const [subscriptionRetryNonce, setSubscriptionRetryNonce] = useState<number>(0);
  const lastSyncedSearchRef = useRef<string | null>(null);

  const retryRequestsSubscription = useCallback(() => {
    setSubscriptionRetryNonce((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setRequests([]);
      setLoading(false);
      setError('Musisz być zalogowany, aby zobaczyć swoje zlecenia.');
      return undefined;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = repairRequestService.subscribeToRequestsForClient(
      currentUser.uid,
      (clientRequests) => {
        setRequests(clientRequests);
        setError(null);
        setLoading(false);
      },
      (err) => {
        console.error('Error subscribing to client repair requests:', err);
        setError('Nie udało się pobrać listy zleceń. Sprawdź połączenie i spróbuj ponownie.');
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [currentUser, subscriptionRetryNonce]);

  const fetchRequests = useCallback(() => {
    if (!currentUser) {
      setLoading(false);
      setError('Musisz być zalogowany, aby zobaczyć swoje zlecenia.');
      return;
    }
    retryRequestsSubscription();
  }, [currentUser, retryRequestsSubscription]);

  const requestBuckets = useMemo(() => getClientRequestBuckets(requests), [requests]);

  const queueBucketByRequestId = useMemo(() => {
    const bucketByRequestId: Record<string, ClientQueueBucket> = {};
    requestBuckets.all.forEach((request) => {
      const requestId = getRepairRequestId(request);
      if (!requestId) {
        return;
      }
      const clientBucket = getClientRequestListBucket(request.status);
      if (clientBucket === 'completed') {
        bucketByRequestId[requestId] = 'history';
        return;
      }
      if (clientBucket === 'needsAction') {
        bucketByRequestId[requestId] = 'attention';
        return;
      }
      bucketByRequestId[requestId] = 'in_progress';
    });

    return bucketByRequestId;
  }, [requestBuckets]);

  const filterCounts = useMemo(() => {
    const counts: Record<ClientListFilter, number> = {
      needsAction: 0,
      active: 0,
      completed: 0,
    };
    requestBuckets.all.forEach((request) => {
      const requestId = getRepairRequestId(request);
      if (!requestId) {
        return;
      }
      const queueBucket = queueBucketByRequestId[requestId];
      if (queueBucket === 'attention') {
        counts.needsAction += 1;
        return;
      }
      if (queueBucket === 'history') {
        counts.completed += 1;
        return;
      }
      counts.active += 1;
    });
    return counts;
  }, [queueBucketByRequestId, requestBuckets]);

  const filteredRequests = useMemo(() => {
    if (!activeFilter) {
      return requestBuckets.all;
    }
    return requestBuckets.all.filter((request) => {
      const requestId = getRepairRequestId(request);
      if (!requestId) {
        return false;
      }
      const queueBucket = queueBucketByRequestId[requestId];
      if (activeFilter === 'needsAction') {
        return queueBucket === 'attention';
      }
      if (activeFilter === 'completed') {
        return queueBucket === 'history';
      }
      return queueBucket === 'in_progress';
    });
  }, [activeFilter, queueBucketByRequestId, requestBuckets]);

  useEffect(() => {
    if (loading) {
      return;
    }
    setActiveRequestId((prev) => {
      if (!prev) return null;
      return filteredRequests.some((request) => getRepairRequestId(request) === prev) ? prev : null;
    });
  }, [filteredRequests, loading]);

  useEffect(() => {
    const nextParams = new URLSearchParams(location.search);
    if (!activeFilter) {
      nextParams.delete('filter');
    } else {
      nextParams.set('filter', activeFilter);
    }
    if (activeRequestId) {
      nextParams.set('request', activeRequestId);
    } else {
      nextParams.delete('request');
    }

    const nextSearch = nextParams.toString();
    const currentSearch = new URLSearchParams(location.search).toString();
    if (nextSearch === currentSearch) {
      lastSyncedSearchRef.current = nextSearch;
      return;
    }
    if (lastSyncedSearchRef.current === nextSearch) {
      return;
    }
    lastSyncedSearchRef.current = nextSearch;

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true }
    );
  }, [activeFilter, activeRequestId, location.pathname, location.search, navigate]);

  const buildDetailHref = useCallback(
    (requestId: string, section?: 'messages') => {
      const returnParams = new URLSearchParams();
      if (activeFilter) {
        returnParams.set('filter', activeFilter);
      }
      returnParams.set('request', requestId);
      const returnTo = `/app/my-requests?${returnParams.toString()}`;

      const detailParams = new URLSearchParams();
      detailParams.set('returnTo', returnTo);
      if (section) {
        detailParams.set('section', section);
      }
      return `/app/request/${requestId}?${detailParams.toString()}`;
    },
    [activeFilter]
  );

  const filterOptions: {
    key: ClientListFilter;
    label: string;
    tone: ClientFilterTone;
    count: number;
  }[] = [
    {
      key: 'needsAction',
      label: 'Do reakcji',
      tone: 'danger',
      count: filterCounts.needsAction,
    },
    {
      key: 'active',
      label: 'W toku',
      tone: 'warning',
      count: filterCounts.active,
    },
    {
      key: 'completed',
      label: 'Historia',
      tone: 'success',
      count: filterCounts.completed,
    },
  ];

  const effectiveRole = getUserRole(userProfile, claims);
  const viewerRole = effectiveRole === UserRole.ADMIN ? UserRole.ADMIN : UserRole.CLIENT;
  const isServiceRequesterView = effectiveRole === UserRole.SERVICE_ADMIN;

  if (loading) {
    return (
      <div className="py-3 md:py-12">
        <div className="mx-auto w-full max-w-6xl space-y-3 px-3 sm:px-4 lg:px-6">
          <PageLoadingState
            title="Ładowanie Twoich zleceń"
            message="Sprawdzamy aktualny stan Twoich spraw."
          />
          <RequestListSkeleton />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-xl px-3 py-8 md:py-12">
        <PageErrorState
          title="Nie udało się pobrać zleceń"
          message={error}
          actionLabel="Spróbuj ponownie"
          onAction={fetchRequests}
        />
      </div>
    );
  }

  return (
    <div className="py-2.5 md:py-10">
      <div className="rs-page-gutter mx-auto w-full max-w-6xl">
        <div className="space-y-2.5 sm:space-y-3">
          <section className="border-b border-primary-dark-300/16 pb-2 pt-0.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <h1 className="flex items-center gap-2 text-[15px] font-semibold text-primary-light-100 sm:text-base">
                  <ClipboardDocumentListIcon className="h-4 w-4 text-ratuj-300 sm:h-5 sm:w-5" />
                  Moje zlecenia
                </h1>
              </div>
              <p className="max-w-full shrink-0 text-right text-xs leading-snug text-primary-light-300 sm:whitespace-nowrap">
                Kolejka Twoich spraw
              </p>
            </div>
          </section>

          <ClientRequestFolderTabs
            options={filterOptions}
            activeFilter={activeFilter}
            onToggle={(nextFilter) => {
              setActiveFilter((prev) => (prev === nextFilter ? null : nextFilter));
            }}
          />

          <section id="client-requests-folder-content" className="space-y-2 sm:space-y-2.5">
            <h2 className="sr-only">Lista Twoich zleceń</h2>
            <div className="space-y-2 sm:space-y-2.5">
              {requests.length === 0 ? (
                isServiceRequesterView ? (
                  <PageEmptyState
                    title="Nie masz jeszcze zleceń"
                    message="Wybierz serwis w Odkrywaj i utwórz zgłoszenie jako zgłaszający."
                    actionLabel="Przejdź do Odkrywaj"
                    onAction={() => navigate('/app/discover')}
                    className="rounded-lg bg-primary-dark-200/14 ring-1 ring-primary-dark-300/18"
                  />
                ) : (
                  <PageEmptyState
                    title="Nie masz jeszcze zleceń"
                    message="Znajdź serwis na mapie i wyślij pierwsze zgłoszenie."
                    actionLabel="Przejdź do mapy serwisów"
                    onAction={() => navigate('/app/discover')}
                    className="rounded-lg bg-primary-dark-200/14 ring-1 ring-primary-dark-300/18"
                  />
                )
              ) : null}

              {requests.length > 0 && filteredRequests.length === 0 ? (
                <PageNoResultsState
                  title="Brak spraw dla aktywnego filtra"
                  message="W tym filtrze nie ma teraz spraw. Wróć do pełnej listy."
                  actionLabel="Wróć do pełnej listy"
                  onAction={() => setActiveFilter(null)}
                  className="rounded-lg bg-primary-dark-200/14 ring-1 ring-primary-dark-300/18"
                />
              ) : null}

              {filteredRequests.length > 0 ? (
                <div className="space-y-2 [touch-action:pan-y] sm:space-y-2.5">
                  {filteredRequests.map((request) => {
                    const cardId = getRepairRequestId(request);
                    if (!cardId) {
                      return null;
                    }
                    return (
                      <RepairRequestCard
                        key={cardId}
                        request={request}
                        currentUserRole={viewerRole}
                        currentUserId={currentUser?.uid}
                        compactListMode
                        disableInlineExpand
                        workspaceSimpleMode
                        fullViewHref={buildDetailHref(cardId)}
                        chatHref={buildDetailHref(cardId, 'messages')}
                        queueBucket={queueBucketByRequestId[cardId]}
                      />
                    );
                  })}
                </div>
              ) : null}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

const ClientRequestFolderTabs: React.FC<{
  options: {
    key: ClientListFilter;
    label: string;
    tone: ClientFilterTone;
    count: number;
  }[];
  activeFilter: ClientListFilter | null;
  onToggle: (nextFilter: ClientListFilter) => void;
}> = ({ options, activeFilter, onToggle }) => (
  <div
    role="tablist"
    aria-label="Filtry listy zleceń klienta"
    data-testid="client-request-filter-tablist"
    className="grid grid-cols-3 gap-1 rounded-xl border border-primary-dark-300/14 bg-primary-dark-100/10 p-1"
  >
    {options.map((option) => {
      const isActive = activeFilter === option.key;
      return (
        <button
          key={option.key}
          type="button"
          role="tab"
          aria-selected={isActive}
          aria-controls="client-requests-folder-content"
          data-filter-tab={option.key}
          data-state={isActive ? 'active' : 'inactive'}
          onClick={() => onToggle(option.key)}
          className={`relative flex min-h-[36px] min-w-0 items-center justify-center overflow-hidden rounded-lg px-2 py-1.5 text-center transition-colors sm:min-h-[38px] sm:px-2.5 ${getClientFilterTabClasses(
            option.tone,
            option.count,
            isActive
          )}`}
        >
          {isActive ? (
            <span
              aria-hidden="true"
              className={`pointer-events-none absolute inset-y-0 left-0 w-[6px] ${getRequestQueueFilterActiveRailClasses(
                CLIENT_FILTER_QUEUE_BUCKET[option.key]
              )}`}
            />
          ) : null}
          <span
            className={`relative z-10 flex min-w-0 items-center justify-center gap-1.5 ${isActive ? 'pl-3' : ''}`}
          >
            <span className="truncate text-[11px] font-medium leading-none text-inherit sm:text-[12px]">
              {option.label}
            </span>
            <span
              className={`shrink-0 text-[12px] font-semibold leading-none tabular-nums ${getClientFilterTabCountClasses(
                option.tone,
                option.count,
                isActive
              )}`}
            >
              {option.count}
            </span>
          </span>
        </button>
      );
    })}
  </div>
);

const getClientFilterTabClasses = (
  tone: ClientFilterTone,
  count: number,
  isActive: boolean
): string => getRequestQueueFilterTabClasses(tone, count, isActive);

const getClientFilterTabCountClasses = (
  tone: ClientFilterTone,
  count: number,
  isActive: boolean
): string => getRequestQueueFilterCountClasses(tone, count, isActive);

const getClientFilterFromSearch = (search: string): ClientListFilter | null => {
  const filter = new URLSearchParams(search).get('filter');
  if (filter === 'needsAction' || filter === 'active' || filter === 'completed') {
    return filter;
  }
  return null;
};

const getClientRequestFromSearch = (search: string): string | null => {
  const requestId = String(new URLSearchParams(search).get('request') || '').trim();
  return requestId || null;
};

export default ClientRequestsPage;
